/**
 * Bounded mapping of durable turn evidence to renderable artifact cards.
 *
 * Only entries the server actually persisted in `turnEvidence.evidence` are
 * mapped — a design turn records `{ designProjectId, artifact, mimeType }`.
 * Malformed entries are dropped, never rendered partially. The emitted `url`
 * is a fixed `data:` URI built from a validated base64 payload and an
 * allowlisted mime type, so no caller-controlled scheme can reach the DOM.
 *
 * Diff cards are intentionally NOT mapped here: the durable schema records
 * `{ path, operation }` per change but no added/removed counts or hunks —
 * rendering a ProductDiffBlock from that would invent fields. The DiffBlocks
 * renderer stays available for surfaces that receive a real diff payload.
 */
import type { ProductArtifactRef } from "@smart-shikka/react";

const ARTIFACT_MIME_ALLOWLIST = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "application/pdf",
]);

/** ~3 MB of decoded bytes expressed as a base64 character bound. */
const ARTIFACT_BASE64_MAX_CHARS = 4_000_000;

const BASE64_RE = /^[A-Za-z0-9+/=\r\n]+$/;

export function mapDurableArtifacts(turnEvidence: unknown): readonly ProductArtifactRef[] | undefined {
  if (typeof turnEvidence !== "object" || turnEvidence === null) return undefined;
  const evidence = (turnEvidence as Record<string, unknown>)["evidence"];
  if (typeof evidence !== "object" || evidence === null) return undefined;
  const e = evidence as Record<string, unknown>;
  const artifact = e["artifact"];
  const mimeType = e["mimeType"];
  const designProjectId = e["designProjectId"];
  if (
    typeof artifact !== "string" ||
    typeof mimeType !== "string" ||
    typeof designProjectId !== "string" ||
    designProjectId.length === 0
  ) {
    return undefined;
  }
  if (!ARTIFACT_MIME_ALLOWLIST.has(mimeType)) return undefined;
  if (artifact.length === 0 || artifact.length > ARTIFACT_BASE64_MAX_CHARS) return undefined;
  if (!BASE64_RE.test(artifact)) return undefined;
  return [
    {
      artifactId: designProjectId,
      name: `Design artifact (${mimeType})`,
      kind: "design-export",
      url: `data:${mimeType};base64,${artifact}`,
    },
  ];
}
